import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  TextField,
  Typography,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { PostcardTemplateData } from '../../types/postcard';
import { templateApi } from '../../services/api';
import { PostcardTemplate } from '../../types/template';
import { PostcardPreview } from './PostcardPreview';

interface PostcardWriterProps {
  postcardImage: string;
  onPostcardsChange: (postcards: PostcardTemplateData[]) => void;
  initialPostcards?: PostcardTemplateData[];
}

const defaultAddress = {
  name: '',
  address_line1: '',
  address_line2: '',
  address_city: '',
  address_state: '',
  address_zip: '',
  address_country: '',
};

export const PostcardWriter: React.FC<PostcardWriterProps> = ({ postcardImage, onPostcardsChange, initialPostcards = [] }) => {
  const [postcards, setPostcards] = useState<PostcardTemplateData[]>(
    initialPostcards.length > 0 ? initialPostcards : [{ message: '', recipientAddress: defaultAddress }]
  );
  const [templates, setTemplates] = useState<PostcardTemplate>({ front: '', rear: '' });

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const data = await templateApi.getPostcardTemplates();
        setTemplates(data.data);
      } catch (error) {
        console.error('Error fetching templates:', error);
      }
    };

    fetchTemplates();
  }, []);

  useEffect(() => {
    onPostcardsChange(postcards);
  }, [postcards, onPostcardsChange]);

  const handleAddPostcard = () => {
    setPostcards([...postcards, { message: '', recipientAddress: defaultAddress }]);
  };

  const handleRemovePostcard = (index: number) => {
    setPostcards(postcards.filter((_, i) => i !== index));
  };

  const handlePostcardChange = (index: number, field: keyof PostcardTemplateData, value: string) => {
    const newPostcards = [...postcards];
    newPostcards[index] = { ...newPostcards[index], [field]: value };
    setPostcards(newPostcards);
  };

  const handleAddressChange = (index: number, field: keyof typeof defaultAddress, value: string) => {
    const newPostcards = [...postcards];
    newPostcards[index] = {
      ...newPostcards[index],
      recipientAddress: {
        ...newPostcards[index].recipientAddress,
        [field]: value,
      },
    };
    setPostcards(newPostcards);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Write Your Postcards
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Add messages and recipient addresses for each postcard. You can add multiple postcards.
      </Typography>

      {postcards.map((postcard, index) => (
        <Card key={index} sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Postcard {index + 1}</Typography>
              {postcards.length > 1 && (
                <IconButton 
                  onClick={() => handleRemovePostcard(index)}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Message"
                  value={postcard.message}
                  onChange={(e) => handlePostcardChange(index, 'message', e.target.value)}
                  placeholder="Write your message here..."
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Recipient Name"
                      value={postcard.recipientAddress.name}
                      onChange={(e) => handleAddressChange(index, 'name', e.target.value)}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address Line 1"
                      value={postcard.recipientAddress.address_line1}
                      onChange={(e) => handleAddressChange(index, 'address_line1', e.target.value)}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address Line 2 (Optional)"
                      value={postcard.recipientAddress.address_line2}
                      onChange={(e) => handleAddressChange(index, 'address_line2', e.target.value)}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="City"
                      value={postcard.recipientAddress.address_city}
                      onChange={(e) => handleAddressChange(index, 'address_city', e.target.value)}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="State"
                      value={postcard.recipientAddress.address_state}
                      onChange={(e) => handleAddressChange(index, 'address_state', e.target.value)}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="ZIP Code"
                      value={postcard.recipientAddress.address_zip}
                      onChange={(e) => handleAddressChange(index, 'address_zip', e.target.value.toUpperCase())}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Country"
                      value={postcard.recipientAddress.address_country}
                      onChange={(e) => handleAddressChange(index, 'address_country', e.target.value)}
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <PostcardPreview
                  postcardImage={postcardImage}
                  frontTemplate={templates.front}
                  rearTemplate={templates.rear}
                  data={postcard}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ))}

      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={handleAddPostcard}
        sx={{ mt: 2 }}
      >
        Add Another Postcard
      </Button>
    </Box>
  );
}; 